/**
 * Analytics Hooks
 *
 * React Query hooks for dashboard analytics, metrics, and real-time data.
 */

import { useQuery } from '@tanstack/react-query';
import api, {
  type DashboardStats,
  type CapacityData,
  type TrendDataPoint,
  type ActivityItem,
  type AgentStatus,
  type TeamMember,
} from '@/lib/api';

// =============================================================================
// Query Keys
// =============================================================================

export const analyticsKeys = {
  all: ['analytics'] as const,
  stats: () => [...analyticsKeys.all, 'stats'] as const,
  capacity: () => [...analyticsKeys.all, 'capacity'] as const,
  trends: (days: number) => [...analyticsKeys.all, 'trends', days] as const,
  activity: (limit: number) => [...analyticsKeys.all, 'activity', limit] as const,
  agents: () => [...analyticsKeys.all, 'agents'] as const,
  team: () => [...analyticsKeys.all, 'team'] as const,
};

// =============================================================================
// Hooks
// =============================================================================

export function useDashboardStats() {
  return useQuery({
    queryKey: analyticsKeys.stats(),
    queryFn: () => api.getDashboardStats(),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });
}

export function useCapacityData() {
  return useQuery({
    queryKey: analyticsKeys.capacity(),
    queryFn: () => api.getCapacityData(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTrends(days = 30) {
  return useQuery({
    queryKey: analyticsKeys.trends(days),
    queryFn: () => api.getTrends(days),
    staleTime: 5 * 60 * 1000,
  });
}

export function useActivity(limit = 20) {
  return useQuery({
    queryKey: analyticsKeys.activity(limit),
    queryFn: () => api.getActivity(limit),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
  });
}

export function useAgentStatus() {
  return useQuery({
    queryKey: analyticsKeys.agents(),
    queryFn: () => api.getAgentStatus(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useTeamMembers() {
  return useQuery({
    queryKey: analyticsKeys.team(),
    queryFn: () => api.getTeamMembers(),
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Mock Data for Development
// =============================================================================

const MOCK_STATS: DashboardStats = {
  activeDecisions: 12,
  activeDecisionsChange: 3,
  pendingApprovals: 5,
  pendingApprovalsChange: 2,
  teamCapacity: 78,
  teamCapacityChange: -5,
  revenueImpact: 2450000,
  revenueImpactChange: 12,
  successRate: 94,
  successRateChange: 2,
  avgResponseTime: 2.4,
  avgResponseTimeChange: -15,
};

const MOCK_CAPACITY: CapacityData = {
  overall: 78,
  teams: [
    { id: 't1', name: 'Platform Engineering', utilization: 92, members: 8, activeProjects: 4 },
    { id: 't2', name: 'Product Development', utilization: 85, members: 12, activeProjects: 6 },
    { id: 't3', name: 'Data Science', utilization: 68, members: 5, activeProjects: 3 },
    { id: 't4', name: 'DevOps', utilization: 74, members: 4, activeProjects: 5 },
    { id: 't5', name: 'QA & Testing', utilization: 61, members: 6, activeProjects: 4 },
    { id: 't6', name: 'Security', utilization: 82, members: 3, activeProjects: 2 },
  ],
};

const generateTrends = (days: number): TrendDataPoint[] => {
  const data: TrendDataPoint[] = [];
  const now = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    const decisions = Math.floor(Math.random() * 15) + 5;
    const approved = Math.floor(decisions * (0.7 + Math.random() * 0.25));
    const rejected = decisions - approved;

    data.push({
      date: date.toISOString().split('T')[0],
      decisions,
      approved,
      rejected,
      successRate: Math.round((approved / decisions) * 100),
    });
  }

  return data;
};

const MOCK_ACTIVITY: ActivityItem[] = [
  {
    id: 'act-1',
    type: 'decision_approved',
    title: 'Database Pooling Approved',
    description: 'Risk mitigation decision approved by Sarah Chen',
    timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    agent: 'risk',
    user: { name: 'Sarah Chen', avatar: undefined },
  },
  {
    id: 'act-2',
    type: 'decision_created',
    title: 'New Revenue Optimization',
    description: 'Revenue Agent identified LinkedIn ad opportunity',
    timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    agent: 'revenue',
  },
  {
    id: 'act-3',
    type: 'alert',
    title: 'Capacity Warning',
    description: 'Platform Engineering team at 92% utilization',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    agent: 'capacity',
  },
  {
    id: 'act-4',
    type: 'decision_executed',
    title: 'Sprint Sync Complete',
    description: 'Cross-team synchronization executed successfully',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    agent: 'coordination',
  },
  {
    id: 'act-5',
    type: 'milestone',
    title: 'Q1 Target Achieved',
    description: 'Revenue optimization decisions exceeded target by 15%',
    timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'act-6',
    type: 'decision_approved',
    title: 'Player Rotation Strategy',
    description: 'Timing recommendation accepted for game optimization',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    agent: 'timing',
  },
  {
    id: 'act-7',
    type: 'decision_rejected',
    title: 'Budget Reallocation Declined',
    description: 'Marketing budget shift postponed for review',
    timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
    agent: 'revenue',
    user: { name: 'Michael Torres' },
  },
  {
    id: 'act-8',
    type: 'decision_created',
    title: 'Resource Reallocation Needed',
    description: 'Capacity Agent detected Project Phoenix risk',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    agent: 'capacity',
  },
];

const MOCK_AGENTS: AgentStatus[] = [
  {
    agent: 'capacity',
    status: 'active',
    lastAction: '2 min ago',
    decisionsToday: 4,
    accuracy: 94,
    confidence: 91,
  },
  {
    agent: 'revenue',
    status: 'active',
    lastAction: '5 min ago',
    decisionsToday: 6,
    accuracy: 89,
    confidence: 87,
  },
  {
    agent: 'risk',
    status: 'active',
    lastAction: '1 min ago',
    decisionsToday: 3,
    accuracy: 96,
    confidence: 94,
  },
  {
    agent: 'timing',
    status: 'idle',
    lastAction: '15 min ago',
    decisionsToday: 2,
    accuracy: 88,
    confidence: 85,
  },
  {
    agent: 'coordination',
    status: 'active',
    lastAction: '8 min ago',
    decisionsToday: 5,
    accuracy: 91,
    confidence: 89,
  },
];

const MOCK_TEAM: TeamMember[] = [
  { id: 'u1', name: 'Sarah Chen', role: 'VP Engineering', utilization: 85, assignedDecisions: 3, status: 'available' },
  { id: 'u2', name: 'Michael Torres', role: 'Product Director', utilization: 72, assignedDecisions: 2, status: 'busy' },
  { id: 'u3', name: 'Emily Rodriguez', role: 'Data Science Lead', utilization: 90, assignedDecisions: 4, status: 'busy' },
  { id: 'u4', name: 'James Kim', role: 'DevOps Manager', utilization: 65, assignedDecisions: 1, status: 'available' },
  { id: 'u5', name: 'Lisa Wang', role: 'Marketing Director', utilization: 78, assignedDecisions: 2, status: 'away' },
];

// Override API methods with mock data in development
if (import.meta.env.DEV) {
  api.getDashboardStats = async () => {
    await new Promise((r) => setTimeout(r, 400));
    return MOCK_STATS;
  };

  api.getCapacityData = async () => {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_CAPACITY;
  };

  api.getTrends = async (days = 30) => {
    await new Promise((r) => setTimeout(r, 500));
    return generateTrends(days);
  };

  api.getActivity = async (limit = 20) => {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_ACTIVITY.slice(0, limit);
  };

  api.getAgentStatus = async () => {
    await new Promise((r) => setTimeout(r, 200));
    return MOCK_AGENTS;
  };

  api.getTeamMembers = async () => {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_TEAM;
  };
}
